import { Link } from "react-router-dom";
import { isPrivateSection, isSpaceSection } from "@al-life/types";
import { useAuth } from "@/features/auth/AuthContext";
import { useSpaceContext } from "@/features/spaces/SpaceContext";
import { liveFeatures, plannedFeatures, type Feature } from "@/features/registry";
import styles from "./HomePage.module.css";

/**
 * A space's home — the dashboard of section cards, scoped to `/:slug`. Members
 * see every section; a non-member viewing a shared space sees only the sections
 * that space made public (with a read-only banner). Links are prefixed with the
 * space slug.
 */
export function SpaceHomePage() {
  const { user } = useAuth();
  const { slug, space, canEdit, isPublicSection } = useSpaceContext();
  const live = liveFeatures();
  const planned = plannedFeatures();

  // A card is visible if you can edit the space, or that section is public.
  // Private-by-design sections (the diary) still show to visitors with a 🔒
  // tag — the page behind it explains the feature without exposing any data.
  const visibleLive = live.filter(
    (f) =>
      canEdit ||
      (f.section && isSpaceSection(f.section) && (isPublicSection(f.section) || isPrivateSection(f.section)))
  );

  return (
    <div className="container">
      <section className={styles.hero}>
        <span className={styles.kicker}>{space?.name ?? slug}</span>
        <h1 className={styles.heroTitle}>
          Our Life, <span className={styles.heroAccent}>Remembered</span>
        </h1>
        <p className={styles.heroSubtitle}>
          A little corner of the internet for the moments that matter most —
          our pets, our travels, the food we make, and the days in between.
        </p>

        {user && canEdit && <p className={styles.greeting}>Welcome back, {user.name} 💛</p>}
        {!canEdit && (
          <p className={styles.greeting}>
            You're viewing <strong>{space?.name ?? slug}</strong>'s space — a read-only look around.
          </p>
        )}
      </section>

      {visibleLive.length > 0 && (
        <section className={styles.section} aria-label="Sections">
          <h2 className={styles.sectionTitle}>Ready to explore</h2>
          <div className={styles.grid}>
            {visibleLive.map((f) => (
              <FeatureCard
                key={f.key}
                feature={f}
                slug={slug}
                locked={!canEdit && Boolean(f.section && isSpaceSection(f.section) && isPrivateSection(f.section))}
              />
            ))}
          </div>
        </section>
      )}

      {canEdit && planned.length > 0 && (
        <section className={styles.section} aria-label="Coming soon">
          <h2 className={styles.sectionTitle}>Coming soon</h2>
          <div className={styles.grid}>
            {planned.map((f) => (
              <FeatureCard key={f.key} feature={f} slug={slug} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function FeatureCard({ feature, slug, locked = false }: { feature: Feature; slug: string; locked?: boolean }) {
  const isLive = feature.status === "live";

  const inner = (
    <>
      <span
        className={styles.cardEmoji}
        style={{ backgroundColor: `${feature.accent}22`, boxShadow: `inset 0 0 0 1px ${feature.accent}55` }}
        aria-hidden="true"
      >
        {feature.emoji}
      </span>
      <div className={styles.cardBody}>
        <h3 className={styles.cardTitle}>
          {feature.title}
          {!isLive && <span className={styles.soonTag}>Soon</span>}
          {locked && <span className={styles.soonTag}>🔒 Private</span>}
        </h3>
        <p className={styles.cardDescription}>{feature.description}</p>
      </div>
      {isLive && (
        <span className={styles.cardArrow} aria-hidden="true">→</span>
      )}
    </>
  );

  if (isLive && feature.section) {
    return (
      <Link
        to={`/${slug}/${feature.section}`}
        className={`${styles.card} ${styles.cardLive}`}
        style={{ ["--accent" as string]: feature.accent }}
      >
        {inner}
      </Link>
    );
  }

  return (
    <div
      className={`${styles.card} ${styles.cardPlanned}`}
      style={{ ["--accent" as string]: feature.accent }}
      aria-disabled="true"
    >
      {inner}
    </div>
  );
}
